import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  PlusCircle,
  Filter as FilterIcon,
  Receipt,
  Building2,
  ShieldAlert,
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import DataTable from '../../../components/DataTable';
import Pagination from '../../../components/Pagination';
import DynamicFormModal from '../../../components/DynamicFormModal';
import FilterDrawer from '../../../components/FilterDrawer';
import AppliedFilters from '../../../components/AppliedFilters';
import ExportExcelButton from '../../../components/ExportExcelButton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { useToast } from '../../../components/ui/toaster.jsx';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import { cn } from '@/lib/utils';
import {
  fetchChargeConcepts,
  fetchChargeConceptOptions,
  createChargeConcept,
  updateChargeConcept,
  deleteChargeConcept,
  toggleChargeConceptStatus,
  exportChargeConcepts,
} from './Services/chargeConceptsService.js';
import {
  columnsExcel,
  MODOS,
  GRUPO_BADGE,
} from './utils/chargeConceptsColumns.jsx';

const ITEMS_PER_PAGE = 10;
const SORTABLE = new Set(['codigo', 'nombre', 'grupo', 'estado', 'updatedAt']);
const MONEDAS = ['USD', 'COP', 'EUR', 'MXN', 'CLP', 'PEN', 'BRL', 'CNY'];

const formatDateTime = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

/**
 * Catálogo de conceptos de cobro.
 *
 * Existe para poder comparar ofertas: sin un código normalizado, cada
 * proveedor escribe lo mismo distinto ("THC NAVIERA", "THC", "Terminal
 * Handling") y la comparación línea a línea no cuadra. Los `alias` son
 * justamente lo que permite reconocer esas variantes al cargar una
 * cotización.
 */
export default function ChargeConceptsPage() {
  const toast = useToast();
  const { tenant } = useAuth();
  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ grupo: 1, orden: 1 });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [options, setOptions] = useState({ grupos: [], basesCobro: [] });

  const fetchAndSet = useCallback(async () => {
    setIsLoading(true);
    try {
      const r = await fetchChargeConcepts({
        limit: ITEMS_PER_PAGE,
        offset: currentPage,
        query: filters,
        sort,
      });
      setDisplayedData(r?.data || []);
      setTotalItems(r?.totalRecords || 0);
    } catch (e) {
      toast.error('Error al cargar conceptos', e?.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, filters, sort, toast]);

  useEffect(() => {
    fetchAndSet();
  }, [fetchAndSet]);

  // Grupos y bases vienen del backend para no duplicar los enums acá.
  useEffect(() => {
    (async () => {
      try {
        const r = await fetchChargeConceptOptions();
        if (r?.success) setOptions(r.data);
      } catch {
        /* si falla, los selectores quedan vacíos y el form lo avisa */
      }
    })();
  }, []);

  const formFields = useMemo(
    () => [
      {
        id: 'codigo',
        label: 'Código',
        type: 'text',
        required: true,
        placeholder: 'THC_DESTINO',
        help: 'Clave de comparación entre ofertas. Mayúsculas y guion bajo.',
      },
      {
        id: 'nombre',
        label: 'Nombre',
        type: 'text',
        required: true,
        placeholder: 'THC Naviera',
      },
      {
        id: 'nombreCorto',
        label: 'Nombre corto',
        type: 'text',
        placeholder: 'THC',
        help: 'Se usa en la matriz de comparación, donde falta ancho.',
      },
      {
        id: 'grupo',
        label: 'Grupo',
        type: 'select',
        required: true,
        options: options.grupos,
      },
      {
        id: 'baseCobroDefault',
        label: 'Base de cobro',
        type: 'select',
        required: true,
        defaultValue: 'Fijo',
        options: options.basesCobro,
      },
      {
        id: 'monedaDefault',
        label: 'Moneda',
        type: 'select',
        defaultValue: 'USD',
        options: MONEDAS.map((m) => ({ value: m, label: m })),
      },
      {
        id: 'tarifaSugerida',
        label: 'Tarifa sugerida',
        type: 'number',
        placeholder: 'Opcional',
      },
      {
        id: 'tarifaMinimaDefault',
        label: 'Tarifa mínima',
        type: 'number',
        placeholder: 'Opcional',
        help: 'Costo mínimo aunque la tarifa sea por peso o volumen.',
      },
      {
        id: 'modos',
        label: 'Aplica a',
        type: 'custom',
        required: true,
        render: (formData, setFormData) => {
          const sel = Array.isArray(formData.modos) ? formData.modos : [];
          const toggle = (v) =>
            setFormData((p) => {
              const actual = Array.isArray(p.modos) ? p.modos : [];
              return {
                ...p,
                modos: actual.includes(v)
                  ? actual.filter((x) => x !== v)
                  : [...actual, v],
              };
            });
          return (
            <div className="flex flex-wrap gap-2">
              {MODOS.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => toggle(m.value)}
                  className={cn(
                    'rounded-md border px-3 py-1.5 text-sm transition-colors',
                    sel.includes(m.value)
                      ? 'border-primary bg-primary/10 text-primary font-medium'
                      : 'border-input text-muted-foreground hover:bg-muted',
                  )}
                >
                  {m.label}
                </button>
              ))}
            </div>
          );
        },
      },
      {
        id: 'aliasesText',
        label: 'Alias',
        type: 'textarea',
        rows: 2,
        placeholder: 'THC, terminal handling, DTHC',
        help: 'Separados por coma. Es como se reconoce el concepto en el documento del proveedor.',
      },
      {
        id: 'aplicaMinimo',
        label: 'Suele tener mínimo',
        type: 'select',
        defaultValue: 'No',
        options: [
          { value: 'No', label: 'No' },
          { value: 'Si', label: 'Sí' },
        ],
      },
      {
        id: 'orden',
        label: 'Orden dentro del grupo',
        type: 'number',
        placeholder: '100',
      },
      {
        id: 'descripcion',
        label: 'Descripción',
        type: 'textarea',
        rows: 2,
      },
      {
        id: 'estado',
        label: 'Estado',
        type: 'select',
        required: true,
        defaultValue: 'Activo',
        options: [
          { value: 'Activo', label: 'Activo' },
          { value: 'Inactivo', label: 'Inactivo' },
        ],
      },
    ],
    [options],
  );

  const filterFields = useMemo(
    () => [
      { id: 'grupo', label: 'Grupo', type: 'select', options: options.grupos },
      {
        id: 'baseCobroDefault',
        label: 'Base de cobro',
        type: 'select',
        options: options.basesCobro,
      },
      {
        id: 'modos',
        label: 'Aplica a',
        type: 'select',
        options: MODOS,
      },
      {
        id: 'estado',
        label: 'Estado',
        type: 'select',
        options: [
          { value: 'Activo', label: 'Activo' },
          { value: 'Inactivo', label: 'Inactivo' },
        ],
      },
    ],
    [options],
  );

  const dataColumns = useMemo(
    () => [
      {
        id: 'codigo',
        label: 'Código',
        sortable: true,
        width: '190px',
        render: (row) => (
          <span className="font-mono text-xs font-medium">{row.codigo}</span>
        ),
      },
      {
        id: 'nombre',
        label: 'Nombre',
        sortable: true,
        render: (row) => (
          <div>
            <span className="font-medium">{row.nombre}</span>
            {row.aliases?.length > 0 && (
              <div className="text-xs text-muted-foreground line-clamp-1">
                {row.aliases.join(' · ')}
              </div>
            )}
          </div>
        ),
      },
      {
        id: 'grupo',
        label: 'Grupo',
        sortable: true,
        width: '190px',
        render: (row) => (
          <Badge
            variant="outline"
            className={cn('font-normal text-xs', GRUPO_BADGE[row.grupo])}
          >
            {row.grupo}
          </Badge>
        ),
      },
      {
        id: 'baseCobroDefault',
        label: 'Base de cobro',
        width: '140px',
        render: (row) => (
          <div className="text-sm">
            {row.baseCobroDefault}
            {row.tarifaMinimaDefault != null && (
              <div className="text-xs text-amber-700">
                mín. {row.monedaDefault} {row.tarifaMinimaDefault}
              </div>
            )}
          </div>
        ),
      },
      {
        id: 'modos',
        label: 'Aplica a',
        width: '130px',
        render: (row) => (
          <div className="flex flex-wrap gap-1">
            {(row.modos || []).map((m) => (
              <Badge key={m} variant="secondary" className="text-[10px] font-normal">
                {MODOS.find((x) => x.value === m)?.label || m}
              </Badge>
            ))}
          </div>
        ),
      },
      { id: 'estado', label: 'Estado', sortable: true, width: '110px', type: 'status' },
      {
        id: 'updatedAt',
        label: 'Actualizado',
        sortable: true,
        width: '150px',
        render: (row) => (
          <span className="text-xs text-muted-foreground">
            {formatDateTime(row.updatedAt)}
          </span>
        ),
      },
    ],
    [],
  );

  const tableColumns = useMemo(
    () => [
      ...dataColumns,
      {
        id: 'actions',
        key: 'actions',
        label: 'Acciones',
        actions: [
          {
            key: 'edit',
            icon: 'Edit',
            tooltip: 'Editar',
            className: 'hover:bg-foreground hover:text-background',
          },
          {
            key: 'toggle',
            icon: 'Power',
            tooltip: (r) => (r.estado === 'Activo' ? 'Inactivar' : 'Activar'),
            className: 'hover:bg-amber-500 hover:text-white',
          },
          {
            key: 'delete',
            icon: 'Trash',
            tooltip: 'Eliminar',
            className: 'hover:bg-destructive hover:text-destructive-foreground',
          },
        ],
      },
    ],
    [dataColumns],
  );

  const handleSort = useCallback((colId) => {
    if (!SORTABLE.has(colId)) return;
    setSort((prev) => {
      const cur = prev[colId];
      if (cur === 1) return { [colId]: -1 };
      if (cur === -1) return { grupo: 1, orden: 1 };
      return { [colId]: 1 };
    });
    setCurrentPage(1);
  }, []);

  const openCreate = () => {
    setCurrentItem(null);
    setIsModalOpen(true);
  };

  /** El modal edita `aliasesText`; el array se arma al guardar. */
  const openEdit = (row) => {
    setCurrentItem({
      ...row,
      aliasesText: (row.aliases || []).join(', '),
      aplicaMinimo: row.aplicaMinimo ? 'Si' : 'No',
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentItem(null);
  };

  const handleSave = async (fd) => {
    // `forbidNonWhitelisted` en el backend rechaza campos que no estén
    // en el DTO, así que `aliasesText` se convierte y se saca del payload.
    const { aliasesText, ...resto } = fd;
    const payload = {
      ...resto,
      aliases: String(aliasesText || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      aplicaMinimo: fd.aplicaMinimo === 'Si',
      modos: Array.isArray(fd.modos) && fd.modos.length ? fd.modos : ['lcl'],
    };
    // Los numéricos vacíos van como null, no como cadena vacía.
    for (const k of ['tarifaSugerida', 'tarifaMinimaDefault', 'orden']) {
      if (payload[k] === '' || payload[k] === undefined) delete payload[k];
    }

    const r = currentItem
      ? await updateChargeConcept(currentItem._id, payload)
      : await createChargeConcept(payload);
    if (r?.success) {
      toast.success(currentItem ? 'Concepto actualizado' : 'Concepto creado');
      closeModal();
      fetchAndSet();
    } else {
      toast.error('No se pudo guardar', r?.message);
    }
  };

  const handleDeleteConfirmation = useCallback((r) => {
    setItemToDelete(r);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleDelete = async () => {
    if (!itemToDelete) return;
    const r = await deleteChargeConcept(itemToDelete._id);
    if (r?.success) {
      toast.success('Concepto eliminado', itemToDelete.codigo);
      if (displayedData.length === 1 && currentPage > 1)
        setCurrentPage((p) => p - 1);
      else fetchAndSet();
    } else {
      toast.error('No se pudo eliminar', r?.message);
    }
    setIsDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleToggleStatus = useCallback(
    async (row) => {
      const r = await toggleChargeConceptStatus(row._id);
      if (r?.success) {
        const isActive = r.data?.estado === 'Activo';
        toast.success(
          isActive ? 'Concepto activado' : 'Concepto inactivado',
          row.codigo,
        );
        fetchAndSet();
      } else {
        toast.error('No se pudo cambiar el estado', r?.message);
      }
    },
    [fetchAndSet, toast],
  );

  const handleTableAction = useCallback(
    (type, row) => {
      if (type === 'edit') return openEdit(row);
      if (type === 'delete') return handleDeleteConfirmation(row);
      if (type === 'toggle') return handleToggleStatus(row);
    },
    [handleDeleteConfirmation, handleToggleStatus],
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-primary/10 p-3 rounded-lg">
              <Receipt className="h-8 w-8 text-primary" />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-2xl">
                  Conceptos de cotización
                </CardTitle>
                {tenant ? (
                  <Badge
                    variant="secondary"
                    className="flex items-center gap-1 font-normal"
                  >
                    <Building2 className="h-3 w-3" />
                    {tenant.nombre || tenant.slug}
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className="flex items-center gap-1 font-normal text-amber-600 border-amber-300"
                  >
                    <ShieldAlert className="h-3 w-3" />
                    Modo plataforma
                  </Badge>
                )}
              </div>
              <CardDescription>
                Conceptos con los que se arman las cotizaciones de proveedor y
                las ofertas al cliente. El código es lo que permite compararlas
                entre sí.
              </CardDescription>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <ExportExcelButton
              service={exportChargeConcepts}
              columns={columnsExcel}
              fileName={`conceptos_cotizacion_${new Date().toISOString().slice(0, 10)}`}
              filters={filters}
            />
            <Button variant="outline" onClick={() => setIsDrawerOpen(true)}>
              <FilterIcon className="mr-2 h-4 w-4" /> Filtros
            </Button>
            <Button onClick={openCreate}>
              <PlusCircle className="mr-2 h-4 w-4" /> Crear concepto
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <AppliedFilters
            filters={filters}
            fields={filterFields}
            onRemoveFilter={(k) =>
              setFilters((p) => {
                const n = { ...p };
                delete n[k];
                return n;
              })
            }
            onClear={() => {
              setFilters({});
              setCurrentPage(1);
            }}
          />
          <DataTable
            columns={tableColumns}
            data={displayedData}
            isLoading={isLoading}
            onAction={handleTableAction}
            sort={sort}
            onSort={handleSort}
            emptyMessage="Sin conceptos"
            emptyHint="Podés sembrar el catálogo base con: node seed-charge-concepts.mjs <slug>"
          />
          {!isLoading && totalItems > 0 && (
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setCurrentPage}
            />
          )}
        </CardContent>
      </Card>

      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        fields={filterFields}
        initialFilters={filters}
        onApply={(a) => {
          setFilters(a);
          setCurrentPage(1);
        }}
      />
      <DynamicFormModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSave={handleSave}
        fields={formFields}
        item={currentItem}
        title={currentItem ? 'Editar concepto' : 'Crear concepto'}
        maxWidth="max-w-3xl"
        twoColumns
      />
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar concepto?</AlertDialogTitle>
            <AlertDialogDescription>
              {itemToDelete && (
                <>
                  Se eliminará <strong>{itemToDelete.nombre}</strong> (
                  {itemToDelete.codigo}). Las cotizaciones que ya lo usan
                  conservan el concepto guardado, pero dejarán de poder
                  compararse por código.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}
